import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';

import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import {
	type RovoContentBridgeAPI,
	type ClientId,
	getBrowserBridgeAPI,
} from '@atlassian/rovo-content-bridge-api';
import {
	ContentDestroyCommand,
	ContentReadyCommand,
	ROVO_CHAT_CLIENT_ID,
	getConfluenceEditorClientId,
} from '@atlassian/rovo-content-bridge-api-commands';

import type { PassThroughCommandProperties } from '../command-handlers/types/command-handler-types';

import { CommandManager } from './command-manager';

export class RovoBridgeCommandManager {
	private clientId: ClientId;
	private api: RovoContentBridgeAPI;
	private commandManager: CommandManager;

	constructor(
		private contentId: string,
		private contentContext: 'staging-area' | 'global',
		editorAPI: PublicPluginAPI<[AnalyticsPlugin, AiStreamingOrchestratorPlugin]>,
	) {
		this.clientId = getConfluenceEditorClientId(this.contentId);
		this.api = getBrowserBridgeAPI();
		this.commandManager = new CommandManager(editorAPI, contentContext, this.clientId, this.api);
	}

	registerCommands(properties: PassThroughCommandProperties) {
		this.commandManager.registerCommands(properties);
		this.commandManager.getCommandHandlers().forEach((handler, command) => {
			this.api.on(this.clientId, command, handler.listen.bind(handler));
		});
	}

	deregisterCommands() {
		this.commandManager.getCommandHandlers().forEach((_, command) => {
			this.api.off(this.clientId, command);
		});
		this.commandManager.reset();
	}

	ready() {
		this.api.ready(this.clientId);
		this.sendContentReadyCommand();
	}

	private sendContentReadyCommand() {
		void this.api.send(ContentReadyCommand, {
			senderId: this.clientId,
			receiverId: ROVO_CHAT_CLIENT_ID,
			contentId: this.contentId,
			contentType: 'page',
			canProvideContext: true,
			contentContext: this.contentContext,
		});
	}

	private sendDestroyCommand() {
		void this.api.send(ContentDestroyCommand, {
			senderId: this.clientId,
			receiverId: ROVO_CHAT_CLIENT_ID,
			contentId: this.contentId,
			contentType: 'page',
			contentContext: this.contentContext,
		});
	}

	teardown() {
		this.sendDestroyCommand();
		this.api.teardown(this.clientId);
	}
}
